import type {
  DataAssetValidationFieldInput,
  DataAssetValidationReportResponse,
  ImportFieldNode,
} from '@erp/rpc';
import { sampleFieldsFromAsset, type SampledRows } from './sampleForSchema';

/**
 * Async work behind the data asset dialogs, deliberately kept outside the
 * components.
 *
 * React Compiler cannot lower `try/finally` or a `throw` inside `try`
 * (`BuildHIR::lowerStatement`), and when it hits one it silently skips
 * memoizing the *entire* component — so a dialog that merely loads a file ends
 * up re-rendering far more than it should. Plain module functions aren't
 * lowered at all, so the error handling lives here and the components get
 * straight-line code plus a result object.
 *
 * Being non-React also makes all of this directly testable.
 */

export type AssetContentFormat = 'csv' | 'json';

export function detectAssetFormat(
  contentType: string,
  fileName: string,
): AssetContentFormat {
  if (
    contentType === 'application/json' ||
    fileName.endsWith('.json') ||
    fileName.endsWith('.jsonl') ||
    fileName.endsWith('.ndjson')
  )
    return 'json';
  return 'csv';
}

export type LoadVersionContentResult =
  | { ok: true; format: AssetContentFormat; content: string }
  | { ok: false; error: string };

/**
 * Downloads a version's contents for editing, pretty-printing JSON when it
 * parses. Never throws — the caller gets a result either way.
 */
export async function loadVersionContent(params: {
  versionId: string;
  contentType: string;
  fileName: string;
  getDownloadUrl: (versionId: string) => Promise<string>;
}): Promise<LoadVersionContentResult> {
  const { versionId, contentType, fileName, getDownloadUrl } = params;

  try {
    const downloadUrl = await getDownloadUrl(versionId);
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      return { ok: false, error: `Failed to load file content` };
    }
    const text = await response.text();

    const format = detectAssetFormat(contentType, fileName);
    let content = text;
    if (format === 'json') {
      const pretty = tryPrettyJson(text);
      if (pretty !== null) content = pretty;
    }

    return { ok: true, format, content };
  } catch {
    return { ok: false, error: 'Failed to load file content' };
  }
}

/** Pretty-printed JSON, or null when the text isn't valid JSON (keep it raw). */
function tryPrettyJson(text: string): string | null {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return null;
  }
}

export type SuggestSchemaResult =
  | {
      ok: true;
      fields: ImportFieldNode[];
      warnings: string[];
      /** Null when the suggestion conforms; otherwise the failing report. */
      failedReport: DataAssetValidationReportResponse | null;
      /** The parsed rows behind the inference; null for JSON sources. */
      sample: SampledRows | null;
    }
  | { ok: false; error: string };

/**
 * Infers fields from a version's contents and proves them against the whole
 * file before they are offered.
 *
 * Inference only ever sees a slice of the file, so it can be wrong in ways the
 * sample can't show. Checking first means a suggestion is either known-good or
 * arrives with the specific reasons it isn't.
 */
export async function suggestSchemaForVersion(params: {
  versionId: string;
  contentType: string;
  fileName: string;
  getDownloadUrl: (versionId: string) => Promise<string>;
  validateFields: (
    fields: DataAssetValidationFieldInput[],
  ) => Promise<DataAssetValidationReportResponse>;
}): Promise<SuggestSchemaResult> {
  const { versionId, contentType, fileName, getDownloadUrl, validateFields } =
    params;

  try {
    const downloadUrl = await getDownloadUrl(versionId);
    const sampled = await sampleFieldsFromAsset({
      downloadUrl,
      format: detectAssetFormat(contentType, fileName),
    });
    if (!sampled.ok) {
      return { ok: false, error: sampled.error };
    }

    const warnings = [...sampled.warnings];
    const report = await validateFields(
      sampled.fields.map((field) => ({
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        validationRules: field.validationRules,
        enumValues: field.enumValues,
      })),
    );

    if (report.conforms) {
      warnings.push(
        `Checked against all ${report.recordsChecked} records in the file — this schema verifies.`,
      );
      return {
        ok: true,
        fields: sampled.fields,
        warnings,
        failedReport: null,
        sample: sampled.sample,
      };
    }

    warnings.push(
      'This suggestion does not fit the whole file. Adjust the fields below, or use Assist in the panel to refine them.',
    );
    return {
      ok: true,
      fields: sampled.fields,
      warnings,
      failedReport: report,
      sample: sampled.sample,
    };
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof Error
          ? error.message
          : 'Could not read the file to sample',
    };
  }
}
